/*jslint node: true*/
"use strict";

var ko = require("pli/knockout");
var numberFormatter = require("pli/util/number").formatNumber;
var pli = require("pli/forms");
var pliObservable = require("pli/forms-knockoutcontrols");

var createContactPercentageFactory = require("../document/contactPercentageViewModel");
var createLeaseRentalInformation = require("../lease-rentalinformation");
var createProvisionsFactory = require("../document-provision");
var dataCache = require("../datacache");
var dateBehaviour = require("pli/forms-knockoutcontrols-date").behaviours.date;
var documentExtensions = require("../document/documentViewModelExtensions");
var entityRegistry = require("../entity-registry");
var selectionForm = require("../common-selectionform");

require("../picklist-area");

//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// LeaseViewModelExtensions class
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//---------------------------------------
function LeaseViewModelExtensions(factories) {
    /// <summary>
    /// Define viewmodel.
    /// </summary>

    // initialize base mixins
    this.initDocument(factories);

    // serializable properties
    this.leaseStatus = pliObservable(null, entityRegistry.leaseStatus.getPicklistBehaviourData(), "picklist");
    this.leaseStatus.isActive = ko.computed(LeaseViewModelExtensions.prototype._computeIsStatusActive, this.leaseStatus);

    this.holdingType = pliObservable(null, dataCache.getObservable("holdingType"), "required", "picklist");
    this.lessorNumber = ko.observable("");
    this.originalNumber = ko.observable("");
    this.area = pliObservable(null, entityRegistry.area.getPicklistBehaviourData(), "required", "picklist");

    this.acquisitionMethod = pliObservable(null, entityRegistry.acquisitionMethod.getPicklistBehaviourData(), "picklist");
    this.acreageEntryType = pliObservable("Acres", dataCache.getObservable("acreageEntryType"), "required", "picklist");
    this.acreageEntryType.publishOn("acreageEntryType");

    this.acquired = pliObservable(null, null, dateBehaviour);
    this.terminated = pliObservable(null, null, dateBehaviour);
    this.leased = pliObservable(null, null, dateBehaviour);
    this.effective = pliObservable(null, null, dateBehaviour);
    this.termType = ko.observable("Years");
    this.term = pliObservable(null, "N0", "numeric");
    this.expired = pliObservable(null, null, dateBehaviour);
    this.extras._effectiveCalculation = ko.computed(LeaseViewModelExtensions.prototype._computeExpiryDate, this);

    this.mainCost = pliObservable(null, "C2", "numeric");
    this.extraCost = pliObservable(null, "C2", "numeric");
    this.cost = ko.computed(LeaseViewModelExtensions.prototype._computeTotalCost, this);
    this.acquisitionPercentage = pliObservable(null, pli.config.editorPrecisionPercentFormat, "numeric");
    this.acquisitionShare = pliObservable(null, "C2", "numeric");
    this.extras._acquisitionShareSubscription = this.acquisitionShare.subscribe(LeaseViewModelExtensions.prototype._computeAcquisitionPercentage, this); // store in field to auto-dispose
    this.extras._acquisitionShareComputation = ko.computed(LeaseViewModelExtensions.prototype._computeAcquisitionShare, this); // store in field to auto-dispose

    this.lessees = factories.lessee.createObservableArray();
    this.lessors = factories.lessor.createObservableArray();
    this.provisions = factories.provisions.createObservableArray();
    this.rental = createLeaseRentalInformation(factories);
    this.extras._firstRentCalculation = ko.computed(LeaseViewModelExtensions.prototype._computeFirstRentDate, this);

    // non-serialized properties
    this.extras.errorsCountRental = ko.computed(LeaseViewModelExtensions.prototype._computeRentalErrorsCount, this);
    this.extras.errorsCountLessees = ko.computed(LeaseViewModelExtensions.prototype._computeLessesErrorsCount, this);
    this.extras.producingStatus = ko.computed(LeaseViewModelExtensions.prototype._computeProducingStatus, this);
}


//---------------------------------------
LeaseViewModelExtensions.prototype.applyServerCalculationResult = function applyLeaseResult(context) {
    this.applyDocumentResult(context);
    context.visit(this.lessors, "lessors");
    context.visit(this.rental, "rental");
};


//---------------------------------------
LeaseViewModelExtensions.prototype.buildServerCalculationData = function buildLeaseData(context) {
    var documentData = this.buildDocumentData(context);
    if (documentData) {
        documentData.acreageEntryType = this.acreageEntryType();
        documentData.area = this.area();
        documentData.holdingType = this.holdingType();
        documentData.lessors = context.visit(this.lessors);
        documentData.rental = context.visit(this.rental);
    }

    return documentData;
};


//---------------------------------------
LeaseViewModelExtensions.prototype.loadViewModel = function loadLease(record) {
    this.loadDocument(record);

    this.leaseStatus.load(record.leaseStatus);
    this.holdingType.load(record.holdingType);
    this.lessorNumber(record.lessorNumber || "");
    this.originalNumber(record.originalNumber || "");
    this.area.load(record.area);

    this.acquisitionMethod.load(record.acquisitionMethod);
    this.acreageEntryType.load(record.acreageEntryType || "Acres");

    this.acquired.load(record.acquired);
    this.terminated.load(record.terminated);
    this.leased.load(record.leased);
    this.effective.load(record.effective);
    this.term.load(record.term);
    this.termType(record.termType || "Years");
    this.expired.load(record.expired);

    this.mainCost.load(record.mainCost);
    this.extraCost.load(record.extraCost);
    this.acquisitionPercentage.load(record.acquisitionPercentage);

    this.lessees.loadViewModel(record.lessees);
    this.lessors.loadViewModel(record.lessors);
    this.provisions.loadViewModel(record.provisions);
    this.rental.loadViewModel(record.rental);
};


//---------------------------------------
LeaseViewModelExtensions.prototype._computeRentalErrorsCount = function computeLeaseRentalErrorsCount() {
    var totalError = (this.lessors().length > 0 && this.lessors.totalPercentage.invalid()) ? 1 : 0;
    return this.rental.extras.errorsCount() + this.lessors.errorsCount() + totalError;
};


//---------------------------------------
LeaseViewModelExtensions.prototype._computeLessesErrorsCount = function computeLeaseLessesErrorsCount() {
    var totalError = (this.lessees().length > 0 && this.lessees.totalPercentage.invalid()) ? 1 : 0;
    return this.lessees.errorsCount() + totalError;
};


//---------------------------------------
LeaseViewModelExtensions.prototype._computeTotalCost = function computeLeaseTotalCost() {
    return numberFormatter((this.mainCost() || 0) + (this.extraCost() || 0), "C2");
};


//---------------------------------------
LeaseViewModelExtensions.prototype._computeAcquisitionPercentage = function computeLeaseAcquisitionPercentage() {
    var percentage, newPercentage,
        total = (this.mainCost() || 0) + (this.extraCost() || 0);

    if (total !== 0) {
        percentage = this.acquisitionPercentage() || 0;
        newPercentage = this.acquisitionShare() * 100 / total;
        if (Math.abs(percentage - newPercentage) > 0.00001) {
            this.acquisitionPercentage(newPercentage);
        }
    }
};


//---------------------------------------
LeaseViewModelExtensions.prototype._computeAcquisitionShare = function computeLeaseAcquisitionShare() {
    var share, newShare,
        total = (this.mainCost() || 0) + (this.extraCost() || 0);

    if (total !== 0) {
        share = this.acquisitionShare() || 0;
        newShare = total * this.acquisitionPercentage() / 100;
        if (Math.abs(share - newShare) > 0.00001) {
            this.acquisitionShare(newShare);
        }
    }
};


//---------------------------------------
LeaseViewModelExtensions.prototype._computeExpiryDate = function computeExpiryDate() {
    var termType = this.termType(), t = this.term(), e = this.effective(), c;
    // set expired date if termType is not custom
    // Expiry date = Effective Date + term - 1 day
    if (t && e && termType !== "Custom") {
        c = new Date(e);
        if (termType === "Months") {
            c.setMonth(e.getMonth() + t);
        } else if (termType === "Years") {
            c.setFullYear(e.getFullYear() + t);
        }
        c.setDate(c.getDate() - 1);
        this.expired(c);
    }
};


//---------------------------------------
LeaseViewModelExtensions.prototype._computeFirstRentDate = function computeFirstRentDate() {
    var d = this.effective(), c;
    if (d) {
        c = new Date(d);
        c.setFullYear(d.getFullYear() + 1);
        c.setDate(c.getDate() - 1); //decrease the date by day
        this.rental.firstRent(c);
    }
};


//-------------------------------------------
LeaseViewModelExtensions.prototype._computeIsStatusActive = function computeIsStatusActive() {
    /// <summary>
    /// Compute whether "this" lease status
    /// is active or not (boolean flag).
    /// </summary>
    var statusDescriptorArray = (this.item && this.item());
    return (statusDescriptorArray && statusDescriptorArray.length > 2 && statusDescriptorArray[2].toUpperCase() === "ACTIVE");
};


//---------------------------------------
LeaseViewModelExtensions.prototype._computeProducingStatus = function computeLeaseProducingStatus() {
    var index,
        producing = false;

    if (this.leaseStatus && this.leaseStatus.isActive()) {
        for (index = 0; !producing && index < this.splits().length; index += 1) {
            if (this.splits()[index].status.isActive() && this.splits()[index].extras.producingStatus()[0] === "P") {
                producing = true;
            }
        }
    }

    return producing ? "Producing" : "Non-producing";
};


//---------------------------------------
LeaseViewModelExtensions.prototype._copyLessees = function copyLessees() {
    /// <summary>
    /// Show selection form that allows users
    /// to find and copy contract percentage to this array.
    /// </summary>
    /// <remarks>
    /// Expects "this" to be a grid observable
    /// array that creates and stores contract percentages.
    /// </remarks>
    selectionForm.open({
        searchApi: "Geo-Document-SearchLessees",
        multiSelect: true,
        text: {
            acceptButton: "Copy",
            iconClass: "sprite-copy-16",
            examples: [
                "<strong>File Numbers</strong>:<br>C-12 will find C-123, C-1234, etc.<br>M001 will find M001, M0019, etc.",
                "<strong>Areas</strong>:<br>river will find GHOST RIVER, TWO RIVERS, etc.",
                "<strong>Lands</strong>:<br>68 17 4 will find Twp 68-Rge 17-W4M<br>94 h 13 c will find 94-H-11-C<br>Note: Sections are not searched",
                "<strong>Combinations</strong>:<br>C, lloyd will find contracts (starting with C) in the Lloydminster area"
            ],
            title: "Copy Lessees"
        },
        onAcceptedContext: this,
        onAccepted: function (newContactPercentageRows) {
            // push to array
            selectionForm.utils.pushRowItems({
                targetArray: this,
                records: newContactPercentageRows,
                rowMap: { contact: 3, silent: 6, percentage: 7 }
            });
        }
    });
};


//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Export
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

module.exports = {
    createFactories: function createExtraLeaseSplitFactories(rootId, createSplitsFactory) {
        return ko.utils.extend({
            lessee: createContactPercentageFactory(rootId, "lessees", true, LeaseViewModelExtensions.prototype._copyLessees), // true => client total percentage calculation
            lessor: createContactPercentageFactory(rootId, "lessors", false), // false => server total percentage calculation
            provisions: createProvisionsFactory(rootId)
        }, documentExtensions.createFactories(rootId, createSplitsFactory));
    },
    mixins: ko.utils.extend({
        applyLeaseResult: LeaseViewModelExtensions.prototype.applyServerCalculationResult,
        buildLeaseData: LeaseViewModelExtensions.prototype.buildServerCalculationData,
        initLease: LeaseViewModelExtensions,
        loadLease: LeaseViewModelExtensions.prototype.loadViewModel
    }, documentExtensions.mixins)
};



//////////////////
// WEBPACK FOOTER
// ./app/lease/leaseViewModelExtensions.js
// module id = 654
// module chunks = 49